import { useController } from 'react-hook-form';
import { ui } from '@procuros/datachecks';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { DisplayModes } from 'components/DataTable/Types';
import { SchemableFormCheckboxGroup } from 'components/Form/SchemableFormCheckboxGroup/SchemableFormCheckboxGroup';
import { useFieldState } from 'components/SchemableForm/hooks/useFieldState';
import { useEnumerableOptions } from 'components/WebEDI/helpers';

type MultiSelectFieldProps = {
  field: ui.EnumerableField;
  fieldId: string;
  isReadOnly: boolean;
  mode: DisplayModes;
  variant?: 'large';
};

export const MultiSelectField = ({ field, fieldId, isReadOnly, mode, variant }: MultiSelectFieldProps) => {
  const { field: formField } = useController({ name: fieldId });
  const { service } = useErrorContext<WebEDIError>();
  const error = service.useErrorById(fieldId);
  const numberOfErrors = service.useNumberOfActiveErrors();
  const setCurrentError = service.setCurrentError;
  const state = useFieldState(fieldId, error);
  const { options } = useEnumerableOptions(field.type_config);

  if (mode === DisplayModes.view) {
    const selectedValues: Array<string> = formField.value ?? [];
    const selectedLabels = selectedValues.map((v) => options.find((o) => o.value === v)?.label).filter(Boolean);

    return (
      <div className="flex flex-col gap-2">
        <span className="text-xs text-gray-500">{field.label}</span>
        {selectedLabels.length > 0 ? (
          <ul className="list-disc pl-4">
            {selectedLabels.map((label, index) => (
              <li key={index} className="text-sm text-gray-700">
                {label}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-sm text-gray-400">&mdash;</span>
        )}
      </div>
    );
  }

  return (
    <SchemableFormCheckboxGroup
      field={field}
      value={formField.value ?? []}
      onChange={formField.onChange}
      onBlur={formField.onBlur}
      error={error}
      totalNumberOfErrors={numberOfErrors}
      onShowFieldErrorModal={() => error && setCurrentError(error)}
      required={field.modality === 'MANDATORY'}
      disabled={isReadOnly}
      fieldState={state}
      variant={variant}
      name={formField.name}
    />
  );
};
